import { motion } from 'framer-motion';
import { X, Download, Brain, Fingerprint, Flame, Compass, Bot, User, GraduationCap, Calendar } from 'lucide-react';
import { useStudentResults } from '@/hooks/useSchoolAdmin';
import { exportCounselorSummary } from '@/lib/results';
import SilhouetteChart from '@/components/assessment/SilhouetteChart';

interface StudentDetailDrawerProps {
  studentUserId: string | null;
  schoolId: string;
  onClose: () => void;
}

const youSections = [
  { key: 'brainWiring' as const, title: 'Brain Wiring', icon: Brain },
  { key: 'signatureStrengths' as const, title: 'Signature Strengths', icon: Fingerprint },
  { key: 'interests' as const, title: 'Interests', icon: Flame },
  { key: 'direction' as const, title: 'Direction', icon: Compass },
  { key: 'aiRealityCheck' as const, title: 'AI Reality Check', icon: Bot },
];

const StudentDetailDrawer = ({ studentUserId, schoolId, onClose }: StudentDetailDrawerProps) => {
  const { data, loading } = useStudentResults(studentUserId, schoolId);

  if (!studentUserId) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/40 z-40"
      />

      {/* Drawer */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed right-0 top-0 h-full w-full max-w-xl bg-background border-l border-border z-50 overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-display font-bold text-foreground">
                {loading ? 'Loading...' : data?.display_name || 'Student'}
              </h2>
              <p className="text-xs text-muted-foreground">{data?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-secondary transition-colors">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>
        ) : !data?.results ? (
          <div className="p-8 text-center text-muted-foreground">
            <p>No assessment results found for this student.</p>
          </div>
        ) : (
          <div className="p-4 space-y-6">
            {/* Meta */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {data.grade && (
                <span className="flex items-center gap-1">
                  <GraduationCap className="w-3 h-3" /> Grade {data.grade}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {new Date(data.created_at).toLocaleDateString()}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                {data.edition}
              </span>
            </div>

            {/* Radar Chart */}
            {data.results.radarData && (
              <SilhouetteChart data={data.results.radarData} />
            )}

            {/* You Summary */}
            <div className="space-y-3">
              {youSections.map((section) => {
                const content = data.results.youSummary[section.key];
                if (!content) return null;
                return (
                  <div key={section.key} className="glass-card p-4 space-y-1">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <section.icon className="w-3.5 h-3.5 text-primary" />
                      {section.title}
                    </h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">{content}</p>
                  </div>
                );
              })}
            </div>

            {/* Career Paths */}
            {data.results.paths && (
              <div className="space-y-3">
                <h3 className="font-display font-bold text-foreground text-sm">Career Paths</h3>
                {data.results.paths.map((path, i) => (
                  <div key={i} className={`glass-card p-4 space-y-2 ${
                    path.riskLevel === 'conservative' ? 'border-l-2 border-accent' :
                    path.riskLevel === 'moderate' ? 'border-l-2 border-primary' :
                    'border-l-2 border-foreground/50'
                  }`}>
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-foreground">{path.name}</h4>
                      <span className="text-[10px] text-muted-foreground uppercase">{path.riskLevel}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{path.description}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Export */}
            <button
              onClick={() => data.results && exportCounselorSummary(data.results, `${data.edition} Edition`)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:brightness-110 transition-all"
            >
              <Download className="w-4 h-4" /> Export Counselor Summary
            </button>
          </div>
        )}
      </motion.div>
    </>
  );
};

export default StudentDetailDrawer;
